import libphonenumber from 'google-libphonenumber'
import moment from 'moment'
import ReactGA from 'react-ga4'
import ReactPixel from 'react-facebook-pixel'
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber
} from 'react-phone-number-input'
import {
  bookee_initiate_checkout,
  eventCategoryOptions
} from '../constants/events'
const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance()

/** Strip leading + / non-digits from a dial-code / phone_prefix value. */
export const normalizeDialCode = prefix =>
  String(prefix || '')
    .replace(/^\+/, '')
    .replace(/\D/g, '')

/**
 * Build an E.164 string from stored phone_prefix + national number.
 * Always includes a leading + when a dial code is present so parsers
 * don't mis-read the number under a defaultCountry (e.g. US).
 */
export const toE164Phone = (prefix, phone) => {
  const rawPhone = String(phone || '').trim()
  if (!rawPhone) return ''
  if (rawPhone.startsWith('+')) {
    try {
      const parsed = parsePhoneNumber(rawPhone)
      if (parsed?.number) return parsed.number
    } catch (_) {
      return rawPhone
    }
  }
  const dial = normalizeDialCode(prefix)
  const national = rawPhone.replace(/\D/g, '')
  if (!national) return ''
  return dial ? `+${dial}${national}` : national
}

/**
 * ISO2 country for a dial code. When multiple countries share a code (+1),
 * prefer `fallback` if it matches, otherwise the first match.
 */
export const countryIsoFromDialCode = (prefix, fallback = 'us') => {
  const dial = normalizeDialCode(prefix)
  const fallbackIso = (fallback || 'us').toLowerCase()
  if (!dial) return fallbackIso
  const matches = getCountries().filter(
    c => String(getCountryCallingCode(c)) === dial
  )
  if (!matches.length) return fallbackIso
  const preferred = matches.find(c => c.toLowerCase() === fallbackIso)
  return (preferred || matches[0]).toLowerCase()
}

// Consent flag: true by default for direct (non-iframe) visits.
// Set to false immediately when running inside an iframe; updated via postMessage from the parent.
let analyticsConsentGranted = true
export const setAnalyticsConsent = granted => {
  analyticsConsentGranted = granted
}

const FBCLID_STORAGE_KEY = 'fbclid'
const DISABLE_STANDARD_VIEW_EVENTS_KEY = 'disable_standard_view_events'
let disableStandardViewEvents = false

export const captureEmbedTrackingFlags = () => {
  if (typeof window === 'undefined') return
  try {
    const params = new URLSearchParams(window.location.search)
    const fbclid = params.get('fbclid')
    if (fbclid) sessionStorage.setItem(FBCLID_STORAGE_KEY, fbclid)
    if (params.get(DISABLE_STANDARD_VIEW_EVENTS_KEY) === '1') {
      disableStandardViewEvents = true
      sessionStorage.setItem(DISABLE_STANDARD_VIEW_EVENTS_KEY, '1')
    } else if (
      sessionStorage.getItem(DISABLE_STANDARD_VIEW_EVENTS_KEY) === '1'
    ) {
      disableStandardViewEvents = true
    }
  } catch {
    // Ignore blocked storage
  }
}

captureEmbedTrackingFlags()

export const validatePassword = password => password.length >= 8
export const isEqualDate = (date1, date2) => {
  return (
    moment(date1).format('YYYY-MM-DD') === moment(date2).format('YYYY-MM-DD')
  )
}

export const validatePhoneNumberGoogleLib = ({ phone, country }) => {
  try {
    const region = (country || '').toUpperCase()
    // Always parse with the region hint — libphonenumber handles both
    // E.164 strings ("+919560895474") and local national strings ("9560895474")
    // correctly when the region is provided.
    return phoneUtil.isValidNumber(phoneUtil.parse(phone, region))
  } catch (_) {
    return false
  }
}

export const validateEmail = email => {
  const regex = /\S+@\S+\.\S{2,}/
  return regex.test(email)
}

export const isFormValidate = (allFields = [], statuses = {}) => {
  return allFields.every(
    field => statuses[field] && statuses[field].type === 'success'
  )
}

export const isFormValidateSignUp = (allFields = [], statuses = {}) => {
  return allFields.every(field => {
    if (field == 'flex' || field == 'affiliate_id') {
      return true
    } else {
      return statuses[field] && statuses[field].type === 'success'
    }
  })
}

export const inIframe = () => {
  try {
    return window.self !== window.top
  } catch (e) {
    return true
  }
}

export const hexToRgbA = (hex, alpha) => {
  let c
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split('')
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]]
    }
    c = '0x' + c.join('')
    return (
      'rgba(' +
      [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') +
      ',' +
      alpha +
      ')'
    )
  }
  throw new Error('Bad Hex')
}

/**
 * Map array of objects by a key value
 * @param {Array} arr
 * @param {String} key
 * @returns {Object}
 */
export function mapByValue(arr, key) {
  return arr.reduce((acc, ob) => ({ ...acc, [ob[key]]: { ...ob } }), {})
}

export class Queue {
  constructor() {
    this.queue = []
  }

  get length() {
    return this.queue.length
  }

  enqueue(item) {
    return this.queue.unshift(item)
  }

  dequeue() {
    return this.queue.pop()
  }

  isEmpty() {
    return this.length === 0
  }

  peek() {
    return this.queue[this.length - 1]
  }
}

export function durationFromSec(sec, enable_format = false, format = 'h:m:s') {
  let data = { h: 0, m: 0, s: 0 }
  if (!sec) {
    sec = 0
  }
  let us = Math.floor(sec)
  let um = Math.floor(sec / 60)
  let uh = Math.floor(um / 60)
  let s = us - um * 60
  let m = um - uh * 60
  let h = uh

  if (h < 10) {
    h = '0' + h
  }
  if (m < 10) {
    m = '0' + m
  }
  if (s < 10) {
    s = '0' + s
  }
  let formated = ''
  if (h === '00') {
    formated = format
      .replace('h', '')
      .replace(':', '')
      .replace('m', m)
      .replace('s', s)
  } else {
    formated = format
      .replace('h', h)
      .replace('m', m)
      .replace('s', s)
  }
  data = { h, m, s }

  if (enable_format) {
    data = formated
  }
  return data
}

export const dataURLtoFile = async (dataurl, filename) => {
  var arr = dataurl.split(','),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], filename, { type: mime })
}

export const stringify_names = data => {
  return data
    .trim()
    .replace(' ', '-')
    .toLowerCase()
}

export const isTextClamped = element => {
  if (!element) return false
  return element.scrollHeight > element.clientHeight
}

export const getFontName = font => {
  const lowerCaseFont = stringify_names(font)
  switch (lowerCaseFont) {
    case 'muli':
      return {
        loaderName: 'Mulish',
        applyName: 'Mulish, sans-serif'
      }
    case 'multi':
      return {
        loaderName: 'Mulish',
        applyName: 'Mulish, sans-serif'
      }
    case 'lato':
      return {
        loaderName: 'Lato',
        applyName: 'Lato, sans-serif'
      }
    case 'raleway':
      return {
        loaderName: 'Raleway',
        applyName: 'Raleway, sans-serif'
      }
    case 'montserrat':
      return {
        loaderName: 'Montserrat',
        applyName: 'Montserrat, sans-serif'
      }
    case 'source-sans-pro':
      return {
        loaderName: 'Source Sans Pro',
        applyName: 'Source Sans Pro, sans-serif'
      }
    case 'source-sans':
      return {
        loaderName: 'Source Sans Pro',
        applyName: 'Source Sans Pro, sans-serif'
      }
    case 'open-sans':
      return {
        loaderName: 'Open Sans',
        applyName: 'Open Sans, sans-serif'
      }
    case 'playfair-display':
      return {
        loaderName: 'Playfair Display',
        applyName: 'Playfair Display, serif'
      }
    default:
      return {
        loaderName: font.split(',')[0].trim(),
        applyName: font
      }
  }
}

export const getExpiryText = (duration, durationType) =>
  `${duration} ${durationType}${duration > 1 ? 's' : ''}`

/**
 * Send events for tracking
 * @param {object} param0 category, action, label, isStandardEvent, eventID
 * @param {object} extraOptions extra properties
 */
export const getCurrencyCode = () =>
  localStorage.getItem('currency_code') || null

/**
 * Fire Meta Pixel InitiateCheckout with ISO currency code and cart value,
 * plus a `begin_checkout` dataLayer event for paid checkouts.
 * @param {{ value?: number|string, contentName?: string, numItems?: number }} params
 * @param {object} tracking extra dataLayer fields (purchase_type, ids, ...)
 */
export const trackInitiateCheckout = (
  { value = 0, contentName, numItems = 1 } = {},
  tracking = {}
) => {
  const currency = getCurrencyCode()
  const parsedValue = parseFloat(value || 0)
  if (parsedValue > 0) {
    dataLayerPush({
      event: 'begin_checkout',
      value: parsedValue,
      ...(currency ? { currency } : {}),
      ...(contentName ? { content_name: contentName } : {}),
      num_items: numItems,
      ...tracking
    })
  }
  if (!currency) return

  trackEvent(
    {
      category: eventCategoryOptions.initiateCheckout,
      action: 'Initiate checkout',
      label: bookee_initiate_checkout,
      isStandardEvent: true
    },
    {
      value: parseFloat(value || 0).toFixed(2),
      currency,
      ...(contentName ? { content_name: contentName } : {}),
      num_items: numItems
    }
  )
}

export const trackEvent = (
  { category, action, label, isStandardEvent = false, eventID = null },
  extraOptions = {}
) => {
  if (!analyticsConsentGranted) return
  const apps = JSON.parse(localStorage.getItem('clientSettings'))?.apps
  const regionName = localStorage.getItem('regionName')
  const locationName = localStorage.getItem('locationName')

  const options = { regionName, locationName, ...extraOptions }

  if (apps) {
    if (apps.google_analytics) {
      ReactGA.gtag('event', label, { action, ...options })
    }
    if (apps.meta_pixel) {
      const isViewContent = category === eventCategoryOptions.viewContent
      const demoteViewStandardEvent =
        disableStandardViewEvents &&
        (category === eventCategoryOptions.schedule ||
          category === eventCategoryOptions.completeRegistration)
      if ((isStandardEvent || isViewContent) && !demoteViewStandardEvent) {
        const pixelOptions = eventID ? { eventID: String(eventID) } : {}
        const pixelData = { action, label, ...options }
        if (isViewContent) {
          const currency = getCurrencyCode()
          if (!currency) return
          pixelData.content_name = pixelData.content_name ?? action
          pixelData.value = parseFloat(pixelData.value || 0).toFixed(2)
          pixelData.currency = currency
        }
        ReactPixel.track(category, pixelData, pixelOptions)
      } else {
        ReactPixel.trackCustom(category, { action, label, ...options })
      }
    }
  }
}

export const dataLayerPush = ({ event, ...extraOptions }) => {
  if (!analyticsConsentGranted) return
  const apps = JSON.parse(localStorage.getItem('clientSettings'))?.apps
  const regionName = localStorage.getItem('regionName')
  const locationName = localStorage.getItem('locationName')

  const options = { regionName, locationName, ...extraOptions }
  const payload = { event, ...options }
  // Independent of the GA app: the host page's GTM decides what to forward.
  // Host GTM cannot read the iframe's dataLayer; embed/index.js pushes it there.
  if (inIframe()) {
    window.parent.postMessage(
      JSON.stringify({ type: 'DataLayerPush', message: payload }),
      '*'
    )
    return
  }
  // Standalone: push when GTM or the GA app is on the page.
  const hasTagManager =
    Array.isArray(window.dataLayer) || !!window.google_tag_manager
  if (!hasTagManager && !apps?.google_analytics) return
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(payload)
}

/**
 * Rounds upto 2 digits
 * @param {number} num number to be rounded
 * @returns rounded number
 */
export const getRoundedNumber = num =>
  Math.round((num + Number.EPSILON) * 100) / 100

/**
 *  Opens google maps with the addreess as query
 * @param {string} address address to be searched
 */
export const openGoogleMaps = ({ address, city, state, pincode, country }) => {
  const url = `https://www.google.com/maps/search/?api=1&query=${address}, ${city}, ${state} ${pincode}, ${country}`
  window.open(url, '_blank')
}

/**
 * Returns the installment amount
 * @param {number} totalAmount total amount
 * @param {number} initialAmount initial amount
 * @param {number} numInstallments number of installments
 * @returns installment amount
 * */
export const getInstallmentAmount = (
  totalAmount,
  initialAmount,
  numInstallments
) => {
  const installmentAmount = (totalAmount - initialAmount) / numInstallments
  return getRoundedNumber(installmentAmount)
}

/**
 * Returns the payment plan details
 * @param {number} totalAmount total amount
 * @param {number} initialAmount initial amount
 * @param {number} numInstallments number of installments
 * @param {number} duration duration
 * @param {string} durationType duration type
 * @returns payment plan details
 * */
export const generatePaymentPlanDetails = (
  totalAmount,
  initialAmount,
  numInstallments,
  duration,
  durationType
) => {
  const installmentAmount = getInstallmentAmount(
    totalAmount,
    initialAmount,
    numInstallments
  )

  let currentPaymentDate = moment()

  const paymentPlan = [
    {
      amount: initialAmount,
      paymentDate: currentPaymentDate.format('MMM DD YYYY'),
      installmentNumber: 'Initial Payment'
    }
  ]
  for (let i = 0; i < numInstallments; i++) {
    currentPaymentDate = moment(currentPaymentDate).add(duration, durationType)

    paymentPlan.push({
      amount: installmentAmount,
      paymentDate: currentPaymentDate.format('MMM DD YYYY'),
      installmentNumber: `Installment ${i + 1}/${numInstallments}`
    })
  }

  return paymentPlan
}

export const mapSharedCentersWithName = ({
  shareCenters,
  centerName,
  centersById,
  region,
  showAll = false
}) => {
  if (shareCenters?.length > 0) {
    // Filter out IDs that don't resolve to a known center to avoid stray
    // commas from undefined branch names in the joined output.
    const centers = [...new Set(shareCenters)].filter(
      centerId => !!centersById?.[centerId]?.branch
    )

    if (centers.length === 0) return centerName

    // Only show region name if there are multiple centers and all are shared
    // For single center regions, show the center name instead
    if (
      centers.length === region?.centers?.length &&
      region?.centers?.length > 1
    )
      return region.name

    return centers
      ?.slice(0, showAll ? centers.length : 2)
      .map(centerId => centersById?.[centerId]?.branch)
      .join(', ')
      .concat(
        !showAll && centers.length > 2
          ? centers.length === 3
            ? ` & ${centersById?.[centers[2]]?.branch}`
            : ` + ${centers.length - 2} other locations`
          : ''
      )
  }
  return centerName
}

export const rotateArrayByOne = arr => {
  if (arr.length <= 1) {
    return [...arr]
  }

  const lastElement = arr[arr.length - 1]
  return [lastElement, ...arr.slice(0, arr.length - 1)]
}

/**
 * Calculate a contrasting color based on the input color
 * @param {string} color - Hex color code (e.g. #FFFFFF) or RGB color (e.g. rgb(255, 255, 255))
 * @returns {string} - Returns a hex color that provides adequate contrast for accessibility
 */
export const getContrastColor = color => {
  let r, g, b

  // Check if color is in RGB format
  if (color.startsWith('rgb')) {
    // Parse RGB values from rgb() or rgba() format
    const rgbMatch = color.match(/rgb\(?\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/)
    if (rgbMatch) {
      r = parseInt(rgbMatch[1], 10)
      g = parseInt(rgbMatch[2], 10)
      b = parseInt(rgbMatch[3], 10)
    }
  } else {
    // Convert hex to rgb
    const hex = color.replace('#', '')
    r = parseInt(hex.substr(0, 2), 16)
    g = parseInt(hex.substr(2, 2), 16)
    b = parseInt(hex.substr(4, 2), 16)
  }

  // Calculate relative luminance according to WCAG 2.0
  const toLinear = channel => {
    const normalized = channel / 255
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4)
  }

  const luminanceR = toLinear(r)
  const luminanceG = toLinear(g)
  const luminanceB = toLinear(b)

  const luminance =
    0.2126 * luminanceR + 0.7152 * luminanceG + 0.0722 * luminanceB

  const getContrastRatio = (lum1, lum2) => {
    const lighter = Math.max(lum1, lum2)
    const darker = Math.min(lum1, lum2)
    return (lighter + 0.05) / (darker + 0.05)
  }

  let rInverse = 255 - r
  let gInverse = 255 - g
  let bInverse = 255 - b

  const luminanceRInverse = toLinear(rInverse)
  const luminanceGInverse = toLinear(gInverse)
  const luminanceBInverse = toLinear(bInverse)
  const luminanceInverse =
    0.2126 * luminanceRInverse +
    0.7152 * luminanceGInverse +
    0.0722 * luminanceBInverse

  let contrastRatio = getContrastRatio(luminance, luminanceInverse)

  if (contrastRatio < 4.5) {
    const luminanceBlack = 0
    const luminanceWhite = 1

    const contrastWithBlack = getContrastRatio(luminance, luminanceBlack)
    const contrastWithWhite = getContrastRatio(luminance, luminanceWhite)

    if (contrastWithBlack >= contrastWithWhite && contrastWithBlack >= 4.5) {
      return '#000000'
    } else if (contrastWithWhite >= 4.5) {
      return '#FFFFFF'
    } else {
      const adjustFactor = luminance > 0.5 ? 0.3 : 0.7

      rInverse = Math.max(0, Math.min(255, Math.round(rInverse * adjustFactor)))
      gInverse = Math.max(0, Math.min(255, Math.round(gInverse * adjustFactor)))
      bInverse = Math.max(0, Math.min(255, Math.round(bInverse * adjustFactor)))
    }
  }

  const toHex = value => {
    const hex = value.toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }

  return `#${toHex(rInverse)}${toHex(gInverse)}${toHex(bInverse)}`
}

export const getCookie = name => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2)
    return parts
      .pop()
      .split(';')
      .shift()
}

export const setCookie = (name, value, days) => {
  let expires = ''
  if (days) {
    const date = new Date()
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
    expires = `; expires=${date.toUTCString()}`
  }
  document.cookie = `${name}=${value || ''}${expires}; path=/`
}

/**
 * Read Meta _fbp/_fbc cookies + contact id for Conversions API match quality.
 * Falls back to building fbc from fbclid if cookie not set yet.
 * @returns {{ fbp: string|null, fbc: string|null, externalId: string|null }}
 */
export const getFbTrackingData = () => {
  if (typeof document === 'undefined') {
    return { fbp: null, fbc: null, externalId: null }
  }

  const fbp = getCookie('_fbp') || null
  let fbc = getCookie('_fbc') || null

  if (!fbc) {
    try {
      const fbclidFromUrl = new URLSearchParams(window.location.search).get(
        'fbclid'
      )
      if (fbclidFromUrl)
        sessionStorage.setItem(FBCLID_STORAGE_KEY, fbclidFromUrl)
      const fbclid = fbclidFromUrl || sessionStorage.getItem(FBCLID_STORAGE_KEY)
      if (fbclid) {
        fbc = `fb.1.${Date.now()}.${fbclid}`
      }
    } catch {
      // Ignore malformed URLs
    }
  }

  let externalId = null
  try {
    const contact = JSON.parse(localStorage.getItem('contact'))
    externalId = contact?.id != null ? String(contact.id) : null
  } catch {
    // Ignore malformed/missing contact data
  }

  return { fbp, fbc, externalId }
}

/**
 * Center utility functions
 */

/**
 * Get center name by center_id from centers data
 * @param {string|number} center_id - The ID of the center
 * @param {Object} centersById - Centers indexed by ID from Redux store
 * @param {Object} regionsDetails - Regions details containing centers from Redux store
 * @returns {string} - Center name, branch name, or 'Unknown Center' if not found
 */
export const getCenterNameById = (
  center_id,
  centersById = null,
  regionsDetails = null
) => {
  if (!center_id) return 'Unknown Center'

  // First try to get from centersById (most efficient)
  if (centersById && centersById[center_id]) {
    return (
      centersById[center_id].name ||
      centersById[center_id].branch ||
      'Unknown Center'
    )
  }

  // Fallback to searching in regionsDetails
  if (regionsDetails && regionsDetails.regions) {
    for (const region of regionsDetails.regions) {
      if (region.centers) {
        const center = region.centers.find(
          c => String(c.id) === String(center_id)
        )
        if (center) {
          return center.name || center.branch || 'Unknown Center'
        }
      }
    }
  }

  return 'Unknown Center'
}

/**
 * Get center object by center_id from centers data
 * @param {string|number} center_id - The ID of the center
 * @param {Object} centersById - Centers indexed by ID from Redux store
 * @param {Object} regionsDetails - Regions details containing centers from Redux store
 * @returns {Object|null} - Center object or null if not found
 */
export const getCenterById = (
  center_id,
  centersById = null,
  regionsDetails = null
) => {
  if (!center_id) return null

  // First try to get from centersById (most efficient)
  if (centersById && centersById[center_id]) {
    return centersById[center_id]
  }

  // Fallback to searching in regionsDetails
  if (regionsDetails && regionsDetails.regions) {
    for (const region of regionsDetails.regions) {
      if (region.centers) {
        const center = region.centers.find(
          c => String(c.id) === String(center_id)
        )
        if (center) {
          return center
        }
      }
    }
  }

  return null
}

/**
 * Get all centers from regions data
 * @param {Object} regionsDetails - Regions details from Redux store
 * @returns {Array} - Array of all centers across all regions
 */
export const getAllCenters = (regionsDetails = null) => {
  if (!regionsDetails || !regionsDetails.regions) return []

  const allCenters = []
  regionsDetails.regions.forEach(region => {
    if (region.centers) {
      allCenters.push(...region.centers)
    }
  })

  return allCenters
}

/**
 * Whether location pickers/labels should show (more than one center).
 * @param {Array} centers
 * @returns {boolean}
 */
export const hasMultipleLocations = (centers = []) => (centers?.length ?? 0) > 1

/**
 * Check if a center exists
 * @param {string|number} center_id - The ID of the center
 * @param {Object} centersById - Centers indexed by ID from Redux store
 * @param {Object} regionsDetails - Regions details containing centers from Redux store
 * @returns {boolean} - True if center exists, false otherwise
 */
export const centerExists = (
  center_id,
  centersById = null,
  regionsDetails = null
) => {
  return getCenterById(center_id, centersById, regionsDetails) !== null
}

/**
 * Remove font-family from inline styles in HTML string
 * @param {string} htmlString - HTML string with inline styles
 * @returns {string} - HTML string with font-family removed from inline styles
 */
export const removeFontFamily = htmlString => {
  if (!htmlString) return ''
  // Remove font-family from inline style attributes
  return htmlString.replace(/style="([^"]*)"/gi, (match, styles) => {
    const cleanedStyles = styles
      .split(';')
      .filter(
        style =>
          !style
            .trim()
            .toLowerCase()
            .startsWith('font-family')
      )
      .join(';')
    return cleanedStyles ? `style="${cleanedStyles}"` : ''
  })
}

// Containers use `white-space: break-spaces`, so editor newlines around list
// tags would otherwise render as blank lines between items.
export const formatRichDescription = htmlString =>
  removeFontFamily(htmlString)
    .replace(/\s*\n\s*(<\/?(?:ul|ol|li)\b)/gi, '$1')
    .replace(/(<\/?(?:ul|ol|li)\b[^>]*>)\s*\n\s*/gi, '$1')

/**
 * Copy a URL to clipboard with support for embedded iframe contexts
 * @param {string} path - The URL path to append to origin (e.g., '/class-detail/123/r/1')
 * @returns {boolean} - Returns true if copy was successful
 */
export const copyToClipboard = path => {
  let url = localStorage.getItem('origin')
  if (!url) {
    url = window.location.href
  } else {
    url = url + '#' + path
  }
  const el = document.createElement('textarea')
  el.value = url
  document.body.appendChild(el)
  el.select()
  document.execCommand('copy')
  document.body.removeChild(el)
  return true
}

const hasProductImage = variant =>
  Array.isArray(variant?.productImage) && variant.productImage.length > 0

export const isVariantInStock = (variant, centerId) => {
  if (!variant?.inventory?.length) return false
  if (centerId != null && centerId !== '') {
    const centerInventory = variant.inventory.find(
      inv => String(inv.center_id) === String(centerId)
    )
    if (centerInventory) return centerInventory.available > 0
  }
  return variant.inventory.some(inv => inv.available > 0)
}

export const isProductInStock = (product, centerId) =>
  product?.variants?.some(variant => isVariantInStock(variant, centerId))

/**
 * Keep relative order, but move products with stock ahead of fully out-of-stock ones.
 * Does not filter/hide any products.
 */
export const sortProductsByStock = (products = [], centerId) => {
  if (!products?.length) return []
  return [...products].sort((a, b) => {
    const aInStock = isProductInStock(a, centerId) ? 0 : 1
    const bInStock = isProductInStock(b, centerId) ? 0 : 1
    return aInStock - bInStock
  })
}

/**
 * Pick the best variant for product listing / initial detail display.
 * Prefers in-stock variants with images, then any variant with an image,
 * then any in-stock variant, then the first variant.
 */
export const getDisplayVariant = (variants = [], centerId) => {
  if (!variants?.length) return null

  const inStockWithImage = variants.find(
    v => isVariantInStock(v, centerId) && hasProductImage(v)
  )
  if (inStockWithImage) return inStockWithImage

  const anyWithImage = variants.find(hasProductImage)
  if (anyWithImage) return anyWithImage

  const inStock = variants.find(v => isVariantInStock(v, centerId))
  if (inStock) return inStock

  const defaultVariant = variants.find(v => v.is_default)
  if (defaultVariant) return defaultVariant

  return variants[0]
}
